import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useDesignTokens } from '@gamesite/game-core'
import { useUserAuthStore } from 'stores/user/auth'
import { getConfig } from 'src/configs/index.js'

// Header and footer toggles share the queue to preserve the order of profile updates.
let saveQueue = Promise.resolve()
const syncStates = new WeakMap()

function getSyncState(authStore) {
  if (!syncStates.has(authStore)) {
    syncStates.set(authStore, { revision: 0, session: 0 })
  }
  return syncStates.get(authStore)
}

export function getInitialTheme(config) {
  const defaultTheme = config.theme ?? 'blue'
  const layoutTheme = config.layoutTheme
  const preference = layoutTheme?.getPreference?.()
  return layoutTheme?.themes?.[preference] ?? defaultTheme
}

export function initLayoutThemeSync(app, config = getConfig()) {
  const layoutTheme = config.layoutTheme
  if (!layoutTheme?.loadPreference) {
    return
  }
  const authStore = useUserAuthStore()
  const state = getSyncState(authStore)
  const { setTheme } = useDesignTokens()
  let requestId = 0
  let initialLoad

  async function loadSettings(id, revision) {
    try {
      const preference = await layoutTheme.loadPreference()
      const theme = layoutTheme.themes[preference]
      if (id !== requestId || revision !== state.revision || !authStore.getAuth || !theme) {
        return
      }
      setTheme(theme)
      layoutTheme.setPreference?.(preference)
    } catch (error) {
      console.error('[LayoutTheme] Failed to load profile theme:', error)
    }
  }

  const stop = watch(
    [() => authStore.getAuth, () => authStore.getUser?.id],
    ([isAuth]) => {
      const id = ++requestId
      state.session++
      if (isAuth) {
        initialLoad = loadSettings(id, state.revision)
      }
    },
    { immediate: true, flush: 'sync' },
  )

  app.onUnmount(() => {
    requestId++
    stop()
  })
  return initialLoad
}

export function useLayoutTheme() {
  const { currentTheme, setTheme } = useDesignTokens()
  const { layoutTheme } = getConfig()
  const themes = layoutTheme?.themes ?? {}
  const authStore = useUserAuthStore()
  const { getAuth, getUser } = storeToRefs(authStore)
  const state = getSyncState(authStore)

  const theme = computed({
    get: () => currentTheme.value,
    set: (value) => {
      const preference = Object.keys(themes).find((key) => themes[key] === value)
      if (!preference || value === currentTheme.value) {
        return
      }

      setTheme(value)
      state.revision++
      layoutTheme.setPreference?.(preference)
      if (!getAuth.value || !layoutTheme.savePreference) {
        return
      }

      const userId = getUser.value?.id
      const session = state.session
      saveQueue = saveQueue
        .then(async () => {
          if (!getAuth.value || getUser.value?.id !== userId || state.session !== session) {
            return
          }

          await layoutTheme.savePreference(preference)
        })
        .catch((error) => {
          console.error('[LayoutTheme] Failed to save profile theme:', error)
        })
    },
  })

  return { theme, themes }
}
